'use client';

import { useState } from 'react';

import Image from 'next/image';

import { CheckCircle, Minus, XCircle } from 'lucide-react';

import { BenefitsSection } from '@/components/sections/benefits-section';
import { CustomerVideos } from '@/components/sections/customer-videos';
import { FaqSection } from '@/components/sections/faq-section';
import {
  BubbleAutopilot,
  BubbleInbound,
  SubpageHero,
} from '@/components/sections/subpage-hero';
import { TemplatesList } from '@/components/sections/templates-list';
import Testimonials from '@/components/sections/testimonials';
import { MagicalText } from '@/components/ui/magical-text';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useTranslation } from '@/lib/i18n/context';
import { cn } from '@/lib/utils';

type ComparisonStatus = 'best' | 'worst' | 'neutral';

interface ComparisonRow {
  metric: string;
  openAI: { value: string; status: ComparisonStatus };
  salesforce: { value: string; status: ComparisonStatus };
}

const CONTENT = {
  en: {
    eyebrow: 'Helpdesks',
    heroTitleA: 'Use Open directly in',
    heroTitleB: 'Service Cloud',
    heroSubtitleA: 'No consultants. No 6-month rollout. Live in under an hour.',
    heroSubtitleB: 'Real resolutions — not just Einstein suggestions.',
    inboundTitle: 'Case #00012345 · Subscription change',
    inboundPreview:
      'Need to switch from Enterprise to Pro starting next month.',
    autopilotTitle: 'Autopilot · case #00012345',
    autopilotSteps: [
      'Read case in Salesforce',
      'Pulled account from Service Cloud',
      'Updated subscription · Pro (May 1)',
      'Logged activity on contact',
      'Closed case · CSAT 5★',
    ],
    moPhotoAlt: 'Mo, Founder of Open',
    vs: 'vs',
  },
  ar: {
    eyebrow: 'أنظمة الدعم',
    heroTitleA: 'استخدم Open داخل',
    heroTitleB: 'Service Cloud',
    heroSubtitleA:
      'بدون استشاريين، وبدون تطبيق يمتد ٦ أشهر. جاهز خلال أقل من ساعة.',
    heroSubtitleB: 'حلول تقفل الحالة فعلاً — مو مجرد اقتراحات من Einstein.',
    inboundTitle: 'حالة #00012345 · تغيير الاشتراك',
    inboundPreview: 'أبي أنتقل من Enterprise لـ Pro من الشهر الجاي.',
    autopilotTitle: 'Autopilot · حالة #00012345',
    autopilotSteps: [
      'قرأ الحالة في Salesforce',
      'سحب الحساب من Service Cloud',
      'حدّث الاشتراك · Pro (١ مايو)',
      'سجّل النشاط على جهة الاتصال',
      'أقفل الحالة · CSAT 5★',
    ],
    moPhotoAlt: 'Mo، مؤسس Open',
    vs: 'مقابل',
  },
} as const;

function SwitchedFromSalesforce() {
  const { t, locale } = useTranslation();
  const c = locale === 'ar' ? CONTENT.ar : CONTENT.en;

  return (
    <div className="mt-20">
      <div className="mx-auto max-w-3xl">
        <div className="flex flex-col items-start gap-6 md:flex-row md:gap-8">
          {/* Mo's photo */}
          <div className="shrink-0">
            <div className="relative">
              <img
                src="/about/image.png"
                alt={c.moPhotoAlt}
                className="h-20 w-20 rounded-full object-cover ring-4 ring-white shadow-xl md:h-24 md:w-24"
              />
              <div className="absolute -bottom-1 -right-1 flex h-7 w-7 items-center justify-center rounded-full bg-black text-xs">
                👋
              </div>
            </div>
          </div>

          {/* Quote */}
          <div className="flex-1">
            <blockquote className="text-lg leading-relaxed text-neutral-700 md:text-xl">
              <span className="font-semibold text-neutral-900">
                {t.helpdesk["Hey, I'm Mo, founder of Open."]}
              </span>{' '}
              {t.helpdesk["Don't take my word for it—companies like"]}{' '}
              <TooltipProvider delayDuration={0}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span
                      className="relative mx-2 inline-flex h-6 w-24 cursor-pointer items-center justify-center align-middle md:h-7 md:w-28"
                      style={{
                        verticalAlign: 'middle',
                        transform: 'translateY(-2px)',
                      }}
                    >
                      <img
                        src="/logos/logo-moneygram.svg"
                        alt="MoneyGram"
                        className="max-h-5 max-w-full object-contain md:max-h-6"
                      />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs px-4 py-3">
                    <p className="font-semibold text-white">MoneyGram</p>
                    <p className="mt-1 text-sm text-neutral-300">
                      {t.helpdesk['Global money transfer company']}
                    </p>
                    <p className="mt-1.5 text-xs text-green-400">
                      65% {t.helpdesk['automation with Open']}
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>{' '}
              {
                t.helpdesk[
                  'chose Open over Salesforce Einstein. They did it because Open works better for them.'
                ]
              }{' '}
              <span className="font-medium text-neutral-900">
                {t.helpdesk['No consultants, no months of setup—just results.']}
              </span>
            </blockquote>
            <div className="mt-4 flex items-center gap-3">
              <span className="text-sm font-semibold text-neutral-900">
                Mo Gharbat
              </span>
              <span className="text-sm text-neutral-500">
                {t.helpdesk['Founder & CEO, Open']}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function SalesforcePage() {
  const { t, locale } = useTranslation();
  const c = locale === 'ar' ? CONTENT.ar : CONTENT.en;
  const [hoveredModel, setHoveredModel] = useState<string | null>(null);

  const comparisonData: ComparisonRow[] = [
    {
      metric: t.helpdesk['Setup Time'],
      openAI: { value: t.helpdesk['Under 1 hour'], status: 'best' },
      salesforce: { value: t.helpdesk['Weeks of setup'], status: 'worst' },
    },
    {
      metric: t.helpdesk['Integration'],
      openAI: { value: t.helpdesk['Native Salesforce user'], status: 'best' },
      salesforce: {
        value: t.helpdesk['Requires consultants'],
        status: 'worst',
      },
    },
    {
      metric: t.helpdesk['Avg Automation Rate'],
      openAI: { value: '77%', status: 'best' },
      salesforce: { value: '~40-50%', status: 'worst' },
    },
    {
      metric: t.helpdesk['Multi-modal AI'],
      openAI: {
        value: t.helpdesk['Voice, Vision, Text, Files'],
        status: 'best',
      },
      salesforce: {
        value: t.helpdesk['Text only (rest gated)'],
        status: 'worst',
      },
    },
    {
      metric: t.helpdesk['AI Quality'],
      openAI: { value: t.helpdesk['Multi-model engine'], status: 'best' },
      salesforce: { value: t.helpdesk['Einstein AI'], status: 'neutral' },
    },
    {
      metric: t.helpdesk['AI Quality Assurance'],
      openAI: { value: t.helpdesk['Built-in automatic QA'], status: 'best' },
      salesforce: { value: t.helpdesk['Manual QA or add-on'], status: 'worst' },
    },
    {
      metric: t.helpdesk['Agentic Workflows'],
      openAI: { value: t.helpdesk['Drag-and-drop builder'], status: 'best' },
      salesforce: { value: t.helpdesk['Rigid workflows'], status: 'worst' },
    },
    {
      metric: t.helpdesk['AI Outbound Sequencing'],
      openAI: {
        value: t.helpdesk['WhatsApp, Phone, Email, SMS'],
        status: 'best',
      },
      salesforce: { value: t.helpdesk['Not available'], status: 'worst' },
    },
    {
      metric: t.helpdesk['Knowledge Gap Detection'],
      openAI: {
        value: t.helpdesk['Auto-detects & suggests content'],
        status: 'best',
      },
      salesforce: {
        value: t.helpdesk['Manual identification'],
        status: 'worst',
      },
    },
    {
      metric: t.helpdesk['Customer Insights'],
      openAI: {
        value: t.helpdesk['Auto Jira tickets, trends'],
        status: 'best',
      },
      salesforce: { value: t.helpdesk['Basic reporting'], status: 'neutral' },
    },
    {
      metric: t.helpdesk['Pricing Model'],
      openAI: { value: t.helpdesk['Outcome-based'], status: 'best' },
      salesforce: { value: t.helpdesk['High licensing fees'], status: 'worst' },
    },
    {
      metric: t.helpdesk['Ease of Use'],
      openAI: { value: t.helpdesk['Simple & intuitive'], status: 'best' },
      salesforce: { value: t.helpdesk['Complex UI'], status: 'worst' },
    },
    {
      metric: t.helpdesk['Learning Capability'],
      openAI: { value: t.helpdesk['Continuous'], status: 'best' },
      salesforce: { value: t.helpdesk['Manual updates'], status: 'worst' },
    },
    {
      metric: t.helpdesk['Observability'],
      openAI: { value: t.helpdesk['Full transparency'], status: 'best' },
      salesforce: { value: t.helpdesk['Limited visibility'], status: 'worst' },
    },
  ];

  return (
    <>
      <SubpageHero
        eyebrow={c.eyebrow}
        title={
          <>
            {c.heroTitleA}{' '}
            <span className="whitespace-nowrap">
              <Image
                src="/helpdicks/salesforce.png"
                alt="Salesforce"
                width={48}
                height={48}
                className="mr-1 inline-block h-[0.8em] w-[0.8em] rounded-[0.18em] align-[-0.1em]"
              />
              <span className="text-[#00A1E0]">Salesforce</span>
            </span>{' '}
            {c.heroTitleB}
          </>
        }
        subtitle={
          <>
            {c.heroSubtitleA}
            <br />
            {c.heroSubtitleB}
          </>
        }
        videoSrc="/home/features/mollie-building.mp4"
        bubbles={[
          {
            role: 'agent',
            widget: (
              <BubbleInbound
                source="Salesforce"
                sourceLogo="/helpdicks/salesforce.png"
                title={c.inboundTitle}
                preview={c.inboundPreview}
              />
            ),
          },
          {
            role: 'agent',
            widget: (
              <BubbleAutopilot
                title={c.autopilotTitle}
                steps={[...c.autopilotSteps]}
              />
            ),
          },
        ]}
      />

      <CustomerVideos />

      <BenefitsSection
        title={t.helpdesk['Get started in minutes']}
        subtitle={
          t.helpdesk[
            'Simple setup, powerful results. No consultants, no complex workflows, no endless customization.'
          ]
        }
        learnMoreLink="#comparison"
        accentColor="blue"
        theme="light"
        benefits={[
          {
            title: t.helpdesk['Create the AI agent in Salesforce'],
            description:
              t.helpdesk[
                'Our AI integrates directly into your Salesforce Service Cloud as a native agent.'
              ],
            linkText: '',
            linkHref: '#',
            videoUrl: '/channels/zendesk/create-the-ai-agent.mp4',
            imagePosition: 'right',
          },
          {
            title: t.helpdesk['Set the routing rules'],
            description:
              t.helpdesk[
                "Route 100% or 10% of your support — it's really up to you."
              ],
            linkText: '',
            linkHref: '#',
            videoUrl: '/channels/zendesk/set-the-routing-rules.mp4',
            imagePosition: 'left',
          },
          {
            title: t.helpdesk['Monitor and iterate'],
            description:
              t.helpdesk[
                'Because your new AI works within Salesforce, all of your reporting dashboards continue to work seamlessly. Continuously improve with feedback and training.'
              ],
            linkText: '',
            linkHref: '#',
            imagePosition: 'right',
            statCard: {
              value: '77%',
              label: t.helpdesk['Automation Rate'],
              sublabel: t.helpdesk['Industry-leading performance'],
            },
          },
        ]}
      />

      {/* Comparison Section */}
      <section className="bg-white py-32">
        <div className="container mx-auto">
          <div className="mx-auto mb-16 flex max-w-3xl flex-col justify-center gap-7 text-center md:text-center">
            <h2 className="font-serif text-2xl md:text-4xl">
              <MagicalText>{t.helpdesk['Our AI']}</MagicalText> {c.vs}{' '}
              {t.helpdesk['Salesforce Einstein AI']}
            </h2>
            <p className="text-muted-foreground text-sm md:text-base">
              {
                t.helpdesk[
                  "See how Open's Agent 5 eliminates Salesforce's complexity and cost."
                ]
              }
            </p>
          </div>
          <div className="relative overflow-hidden p-8">
            <div className="border-border/50 bg-background/50 relative overflow-hidden border backdrop-blur-sm">
              <Table>
                <TableHeader>
                  <TableRow className="border-border/50">
                    <TableHead className="text-foreground font-semibold">
                      {t.helpdesk['Feature']}
                    </TableHead>
                    <TableHead className="text-foreground text-center font-semibold">
                      <MagicalText>{t.helpdesk['Our AI']}</MagicalText>{' '}
                      {t.helpdesk['(Open)']}
                    </TableHead>
                    <TableHead className="text-foreground text-center font-semibold">
                      {t.helpdesk['Salesforce Einstein AI']}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {comparisonData.map((row, index) => (
                    <TableRow
                      key={index}
                      className="border-border/30 hover:bg-muted/30 transition-colors"
                    >
                      <TableCell className="text-foreground py-4 font-medium">
                        {row.metric}
                      </TableCell>
                      <TableCell
                        className={cn(
                          'cursor-pointer py-4 text-center font-medium transition-all duration-300',
                          row.openAI.status === 'best'
                            ? 'bg-green-50 text-green-600 dark:bg-green-950/20'
                            : row.openAI.status === 'worst'
                              ? 'bg-red-50 text-red-600 dark:bg-red-950/20'
                              : 'bg-muted/50 text-foreground',
                          hoveredModel === 'openAI' &&
                            'bg-green-50/80 dark:bg-green-950/30',
                        )}
                        onMouseEnter={() => setHoveredModel('openAI')}
                        onMouseLeave={() => setHoveredModel(null)}
                      >
                        <div className="flex items-center justify-center gap-2">
                          {row.openAI.status === 'best' && (
                            <CheckCircle className="h-4 w-4 text-green-600" />
                          )}
                          {row.openAI.status === 'worst' && (
                            <XCircle className="h-4 w-4 text-red-600" />
                          )}
                          {row.openAI.status === 'neutral' && (
                            <Minus className="text-muted-foreground h-4 w-4" />
                          )}
                          <span>{row.openAI.value}</span>
                        </div>
                      </TableCell>
                      <TableCell
                        className={cn(
                          'cursor-pointer py-4 text-center font-medium transition-all duration-300',
                          row.salesforce.status === 'best'
                            ? 'bg-green-50 text-green-600 dark:bg-green-950/20'
                            : row.salesforce.status === 'worst'
                              ? 'bg-red-50 text-red-600 dark:bg-red-950/20'
                              : 'bg-muted/50 text-foreground',
                          hoveredModel === 'salesforce' &&
                            'bg-red-50/80 dark:bg-green-950/30',
                        )}
                        onMouseEnter={() => setHoveredModel('salesforce')}
                        onMouseLeave={() => setHoveredModel(null)}
                      >
                        <div className="flex items-center justify-center gap-2">
                          {row.salesforce.status === 'best' && (
                            <CheckCircle className="h-4 w-4 text-green-600" />
                          )}
                          {row.salesforce.status === 'worst' && (
                            <XCircle className="h-4 w-4 text-red-600" />
                          )}
                          {row.salesforce.status === 'neutral' && (
                            <Minus className="text-muted-foreground h-4 w-4" />
                          )}
                          <span>{row.salesforce.value}</span>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>

          {/* Social Proof - Companies that chose Open over Salesforce Einstein */}
          <SwitchedFromSalesforce />
        </div>
      </section>

      {/* Automate Salesforce Manual Work Section */}
      <section className="bg-neutral-50 py-32">
        <div className="container mx-auto">
          <div className="mx-auto mb-16 flex max-w-3xl flex-col justify-center gap-7 text-center md:text-center">
            <h2 className="font-serif text-2xl md:text-4xl">
              {t.helpdesk['Automate the manual tasks, not just the answers']}
            </h2>
            <p className="text-muted-foreground text-sm md:text-base">
              {
                t.helpdesk[
                  'In addition to fully resolving support tickets, Open automates the manual tasks that slow down your team—routing, tagging, QA, and more.'
                ]
              }
            </p>
          </div>
          <TemplatesList
            systemFilter="Salesforce"
            categoryFilter="Customer Support"
            limit={6}
            variant="slider"
          />
        </div>
      </section>

      {/* FAQ Section */}
      <FaqSection
        title={t.helpdesk['Frequently asked questions']}
        subtitle={
          t.helpdesk[
            "Find answers to common questions about our Salesforce AI agent. Can't find what you're looking for? Contact our support team."
          ]
        }
        items={[
          {
            question:
              t.helpdesk[
                'How long does it take to set up Open with Salesforce?'
              ],
            answer:
              t.helpdesk[
                'Open can be set up in under an hour. It connects directly to your Salesforce Service Cloud via API and starts resolving queries on day one using your existing help center content.'
              ],
          },
          {
            question:
              t.helpdesk['Do I need Salesforce consultants to set up Open?'],
            answer:
              t.helpdesk[
                'No. Unlike Salesforce Einstein which often requires consultant setup, Open can be deployed in under an hour without any external help or Salesforce expertise.'
              ],
          },
          {
            question: t.helpdesk['Does Open support Salesforce Email-to-Case?'],
            answer:
              t.helpdesk[
                'Yes. Open fully supports Salesforce Email-to-Case and can automatically respond to customer emails that create cases in your Salesforce Service Cloud.'
              ],
          },
          {
            question:
              t.helpdesk[
                'Can I test Open and Salesforce Einstein at the same time?'
              ],
            answer:
              t.helpdesk[
                'Yes! We can set up Open for you in a way where you can run an A/B test and decide for yourself. Try it risk-free for 1 month.'
              ],
          },
          {
            question: t.helpdesk['Can I try Open risk-free?'],
            answer:
              t.helpdesk[
                'Absolutely. You can try Open risk-free for a month with free implementation. Our team will help you get set up and see results before you commit.'
              ],
          },
          {
            question:
              t.helpdesk[
                'Does Open work with Salesforce MIAW (Messaging in App Window)?'
              ],
            answer:
              t.helpdesk[
                'Yes. Open integrates seamlessly with Salesforce MIAW (Messaging in App Window), also known as Salesforce chat. Your customers can get instant AI-powered support through the Salesforce chat interface.'
              ],
          },
          {
            question:
              t.helpdesk['Is Open easier to use than Salesforce Einstein?'],
            answer:
              t.helpdesk[
                'Absolutely. While Salesforce Einstein requires deep Salesforce knowledge and complex configuration, Open is designed for simplicity with an intuitive interface and zero learning curve.'
              ],
          },
          {
            question:
              t.helpdesk[
                'Can I see how Open is performing in Salesforce reports?'
              ],
            answer:
              t.helpdesk[
                'Yes. Open works as a native Salesforce agent, so all your existing dashboards, reports, and analytics continue to work perfectly. Track AI performance alongside your human agents.'
              ],
          },
          {
            question: t.helpdesk['How does Open handle Salesforce workflows?'],
            answer:
              t.helpdesk[
                'Open respects your existing Salesforce workflows, assignment rules, and escalation paths. It works within your current setup without requiring workflow redesign.'
              ],
          },
          {
            question: t.helpdesk['How does Open hand off to human agents?'],
            answer:
              t.helpdesk[
                'Open updates Salesforce cases, adds AI-generated summaries, and seamlessly hands off to human agents when needed—giving them full context to respond faster.'
              ],
          },
          {
            question: t.helpdesk['How does Open learn and improve over time?'],
            answer:
              t.helpdesk[
                "Open features continuous learning capabilities with built-in evaluation tools. It learns from your team's feedback, analyzes historical cases, and improves accuracy over time with observability tools that provide full transparency."
              ],
          },
          {
            question:
              t.helpdesk['What channels can Open handle with Salesforce?'],
            answer:
              t.helpdesk[
                'Open is omnichannel and works across Salesforce cases, Email-to-Case, MIAW chat, voice, WhatsApp, and other messaging channels—all from one unified AI engine.'
              ],
          },
          {
            question:
              t.helpdesk[
                'Does Open require changes to my current Salesforce setup?'
              ],
            answer:
              t.helpdesk[
                'No. Open connects via API with no migration required. It works within your existing workflows, follows your assignment rules, and requires zero disruption to your current Salesforce configuration.'
              ],
          },
        ]}
      />

      {/* Testimonials Section */}
      <Testimonials />
    </>
  );
}
